import React, { useMemo } from 'react';
import { Button, Grid, Paper, Box, Typography, TextField } from 'components/mui';
import { useCustomerFormUnified } from 'containers/pages/customers/hooks';
import * as TY from 'containers/pages/customers/constants';
import CustomerTypeSelector from '../customerTypeSelector/CustomerTypeSelector';
import LoadingBackdrop from 'components/LoadingBackdrop';
import { CustomerType } from 'containers/pages/customers/types';

interface CustomerFormUnifiedProps {
  mode: 'add' | 'edit';
  customerId?: number | null;
  customerTypes: CustomerType[];
  onSave: (formData: Record<string, unknown>, isEdit: boolean) => Promise<void>;
  onClose?: () => void;
}

function MissingFieldsHint({ canNotSave, missing }: { canNotSave: boolean; missing: string[] }) {
  if (!canNotSave || missing.length === 0) return null;
  return (
    <Typography color="error" variant="body2" sx={{ mb: 1, textAlign: 'right' }}>
      Please fill in all required fields: {missing.join(', ')}.
    </Typography>
  );
}

export default function CustomerFormUnified({
  mode,
  customerId,
  customerTypes,
  onSave,
  onClose,
}: CustomerFormUnifiedProps) {
  const { state, handler } = useCustomerFormUnified({
    mode,
    customerId,
    onSave,
    onClose,
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const customer = state.customer as any;

  const missingFields = useMemo(() => {
    const missing: string[] = [];
    if (!customer[TY.NAME]) missing.push('Name');
    if (!customer[TY.TYPE_ID]) missing.push('Type');
    return missing;
  }, [customer]);

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        '& .section + .section': { mt: 2 },
      }}
    >
      <Grid container spacing={2}>
        {/* LEFT COLUMN — Identity & Address */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap={2} sx={{ height: '100%' }}>
            <TextField
              label="Customer Name"
              name={TY.NAME}
              value={customer[TY.NAME] || ''}
              onChange={handler.onForm}
              required
              error={state.valid.Name.isErr}
              helperText={state.valid.Name.errMsg}
              fullWidth
              size="small"
            />
            <CustomerTypeSelector
              value={customer[TY.TYPE_ID] as number | null}
              customerTypes={customerTypes}
              onChange={handler.onTypeChange}
              error={state.valid.TypeId.isErr}
              helperText={state.valid.TypeId.errMsg}
            />
            <TextField
              label="Address"
              name={TY.ADDRESS}
              value={customer[TY.ADDRESS] || ''}
              onChange={handler.onForm}
              multiline
              minRows={3}
              fullWidth
              size="small"
              sx={{ flex: 1, '& .MuiInputBase-root': { height: '100%', alignItems: 'flex-start' } }}
            />
          </Box>
        </Grid>

        {/* RIGHT COLUMN — Contact Info */}
        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Work Phone"
              name={TY.WORK_PHONE}
              value={customer[TY.WORK_PHONE] || ''}
              onChange={handler.onForm}
              fullWidth
              size="small"
            />
            <TextField
              label="Cell Phone"
              name={TY.CELL_PHONE}
              value={customer[TY.CELL_PHONE] || ''}
              onChange={handler.onForm}
              fullWidth
              size="small"
            />
            <TextField
              label="Work Email"
              name={TY.WORK_EMAIL}
              value={customer[TY.WORK_EMAIL] || ''}
              onChange={handler.onForm}
              error={state.valid.WorkEmail.isErr}
              helperText={state.valid.WorkEmail.errMsg}
              fullWidth
              size="small"
            />
            <TextField
              label="Other Email"
              name={TY.OTHER_EMAIL}
              value={customer[TY.OTHER_EMAIL] || ''}
              onChange={handler.onForm}
              error={state.valid.OtherEmail.isErr}
              helperText={state.valid.OtherEmail.errMsg}
              fullWidth
              size="small"
            />
          </Box>
        </Grid>
      </Grid>

      {/* Action Row */}
      <Box display="flex" gap={2} mt={3} alignItems="flex-start">
        <Box flex="1" />
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <MissingFieldsHint canNotSave={state.canNotSave} missing={missingFields} />
          <Box display="flex" gap={2}>
            <Button
              variant="contained"
              disabled={state.canNotSave}
              onClick={handler.onSave}
              title={
                state.canNotSave && missingFields.length > 0
                  ? `Please fill in: ${missingFields.join(', ')}`
                  : undefined
              }
            >
              {mode === 'edit' ? 'SAVE' : 'CREATE'}
            </Button>
            <Button variant="outlined" onClick={handler.onReset}>
              RESET
            </Button>
          </Box>
        </Box>
      </Box>

      <LoadingBackdrop open={state.isLoading} />
    </Paper>
  );
}
